import React from "react";
import { Box, Grid, Typography, Container, Card, Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import offplanbanner from "@assets/images/2-Real-Estate-Banner.jpg";
import Offplan from "@assets/images/Apartment-Al-Bahar.webp";
import SEO from "../../../utils/SeoComponent";

import {
  FaBuilding,
  FaHandshake,
  FaChartLine,
  FaCreditCard,
  FaStar,
  FaShieldAlt,
  FaPercentage,
  FaGlobeAmericas,
} from "react-icons/fa";
import ContactSection from "../Home/contactSection";
import { useNavigate } from "react-router-dom";

// Styled Components
const PageSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  position: "relative",
  overflow: "hidden",
}));

const HeroSection = styled(PageSection)(({ theme }) => ({
  backgroundColor: "#f8f9fa",
  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(${offplanbanner})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  color: "white",
  padding: theme.spacing(15, 0),
  textAlign: "center",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "150px",
    background: "linear-gradient(to top, #fff, transparent)",
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  position: "relative",
  fontWeight: 700,
  marginBottom: theme.spacing(6),
  paddingBottom: theme.spacing(2),
  display: "inline-block",
  "&:after": {
    content: '""',
    position: "absolute",
    bottom: "-5px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "80px",
    height: "4px",
    backgroundColor: "#C10E14",
    transition: "all 0.3s ease-in-out",
    [theme.breakpoints.up("md")]: {
      left: 0,
      transform: "none",
    },
  },
}));

const FeatureItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  marginBottom: theme.spacing(4),
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  marginRight: theme.spacing(2),
  backgroundColor: "#f8f9fc",
  borderRadius: "50%",
  padding: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "1.5rem",
  color: "#C10E14",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
}));

const InfoCard = styled(Card)(({ theme }) => ({
  height: "100%",
  padding: theme.spacing(4),
  borderRadius: "15px",
  backgroundColor: "#f8f9fc",
  boxShadow: "0 5px 15px rgba(0, 0, 0, 0.05)",
}));

const OffPlanPage = () => {
  const navigate = useNavigate();
  // Benefits of Off-Plan Investment
  const benefits = [
    {
      icon: <FaPercentage />,
      title: "Lower Initial Costs",
      description:
        "Often sold at lower prices compared to completed properties.",
    },
    {
      icon: <FaChartLine />,
      title: "High Potential for Appreciation",
      description:
        "Properties can increase in value significantly by completion, offering strong capital appreciation.",
    },
    {
      icon: <FaCreditCard />,
      title: "Payment Flexibility",
      description:
        "Developers usually offer flexible payment plans over time, making it easier for investors.",
    },
    {
      icon: <FaStar />,
      title: "Modern Features",
      description:
        "New developments incorporate the latest designs, technology, and sustainability features.",
    },
    {
      icon: <FaHandshake />,
      title: "Investor Incentives",
      description:
        "Dubai offers tax benefits, attractive financing options, and easy ownership regulations.",
    },
    {
      icon: <FaShieldAlt />,
      title: "Secure Legal Framework",
      description:
        "Dubai's property laws protect off-plan buyers, ensuring transparency and reliability.",
    },
    {
      icon: <FaGlobeAmericas />,
      title: "Growing Market",
      description:
        "Dubai's real estate market continues to expand with high demand for properties.",
    },
    {
      icon: <FaBuilding />,
      title: "Attractive Rental Yields",
      description:
        "With growing population and tourism, rental yields can be strong even for off-plan properties.",
    },
  ];

  return (
    <>
      <SEO slug={"offplan"} />

      <Box className="offplan-page">
        {/* Hero Section */}
        <HeroSection className="hero-section">
          <Container
            maxWidth="md"
            sx={{
              mt: { xs: 6, md: 14 }, // small screens: mt-6, medium and up: mt-14
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                fontWeight={700}
              >
                Off-Plan Properties in Dubai
              </Typography>
              <Typography
                variant="h5"
                component="p"
                sx={{ mb: 4, opacity: 0.9 }}
              >
                Invest in Dubai's future with premium off-plan properties
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  const element = document.getElementById("properties");
                  if (element) {
                    element.scrollIntoView({ behavior: "smooth" });
                  }
                }}
                sx={{
                  borderRadius: "50px",
                  py: 1.5,
                  px: 4,
                  fontSize: "1rem",
                  boxShadow: "0 4px 14px rgba(0, 0, 0, 0.25)",
                  backgroundColor: "#C10E14", // Set the background color to #C10E14
                  "&:hover": {
                    backgroundColor: "#A30B11", // Set the hover background color to #A30B11
                  },
                }}
              >
                Explore Properties
              </Button>
            </motion.div>
          </Container>
        </HeroSection>

        {/* What is Off-Plan Section */}
        <PageSection
          id="properties"
          className="what-is-offplan-section"
          sx={{ bgcolor: "#fff" }}
        >
          <Container>
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
                    <SectionTitle variant="h4" component="h2">
                      What is Off-Plan?
                    </SectionTitle>
                  </Box>
                  <InfoCard>
                    <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                      <Typography component="li" sx={{ mb: 2 }}>
                        Off-plan refers to properties that are sold before or
                        during construction, typically based on architectural
                        plans and models.
                      </Typography>
                      <Typography component="li" sx={{ mb: 2 }}>
                        Buyers invest in these properties while they are still
                        being developed, often before the completion date.
                      </Typography>
                      <Typography component="li">
                        Off-plan properties may offer customization options
                        depending on the developer's policy.
                      </Typography>
                    </Box>
                  </InfoCard>
                  <Box sx={{ mt: 4, textAlign: { xs: "center", md: "left" } }}>
                    <Button
                      onClick={() => navigate("/contact-us")}
                      variant="contained"
                      size="large"
                      sx={{
                        borderRadius: "50px",
                        py: 1.5,
                        px: 4,
                        fontSize: "1rem",
                        boxShadow: "0 4px 14px rgba(0, 0, 0, 0.15)",
                        backgroundColor: "#C10E14", // Set the background color to #C10E14
                        "&:hover": {
                          backgroundColor: "#A30B11", // Set the hover background color to #A30B11
                        },
                      }}
                    >
                      Schedule a Consultation
                    </Button>
                  </Box>
                </motion.div>
              </Grid>
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        width: "80%",
                        height: "80%",
                        bottom: "-20px",
                        right: "-20px",
                        borderRadius: "10px",
                        background: "linear-gradient(45deg, #2196F3, #3F51B5)",
                        zIndex: -1,
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={Offplan}
                      alt="Off-Plan Properties"
                      sx={{
                        width: "100%",
                        borderRadius: "10px",
                        boxShadow: "0 15px 35px rgba(0, 0, 0, 0.2)",
                      }}
                    />
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </PageSection>

        {/* Benefits Section */}
        <PageSection className="benefits-section" sx={{ bgcolor: "#f8f9fc" }}>
          <Container>
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Typography
                variant="h4"
                component="h2"
                fontWeight={700}
                sx={{ mb: 2 }}
              >
                Why Invest in Off-Plan in Dubai?
              </Typography>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ maxWidth: "700px", mx: "auto" }}
              >
                Discover the advantages of investing in Dubai's premium off-plan
                properties
              </Typography>
            </Box>

            <Grid container spacing={4}>
              {benefits.map((benefit, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <FeatureItem>
                      <IconWrapper>{benefit.icon}</IconWrapper>
                      <Box>
                        <Typography
                          variant="h6"
                          component="h3"
                          fontWeight={600}
                          gutterBottom
                        >
                          {benefit.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {benefit.description}
                        </Typography>
                      </Box>
                    </FeatureItem>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </PageSection>

        {/* Contact Section */}
        <Box
          sx={{
            padding: "80px 0px",
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(${offplanbanner})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          <ContactSection />
        </Box>
      </Box>
    </>
  );
};

export default OffPlanPage;
